/**
 * The equation condition: rows joined by "all" or "any". A row is one check.
 * This module owns the shape and the words for it. The rules a row must pass
 * against its graph are in equation-rules.ts. When and how a row is read
 * during a call is the worker's.
 */
import { z } from 'zod';
import { snakeKey } from './keys.js';
const uuid = z.string().uuid();
/* ---------- operators and value types ---------- */
export const EQUATION_OPERATORS = [
    'equals',
    'not_equals',
    'greater_than',
    'less_than',
    'greater_or_equal',
    'less_or_equal',
    'contains',
    'not_contains',
    'exists',
    'not_exists',
];
/** The operators that hold while a value is missing. */
export const NEGATIVE_OPERATORS = [
    'not_equals',
    'not_contains',
    'not_exists',
];
/** The operators that take no comparison value. */
export const PRESENCE_OPERATORS = [
    'exists',
    'not_exists',
];
const ORDERED = [
    'equals',
    'not_equals',
    'greater_than',
    'less_than',
    'greater_or_equal',
    'less_or_equal',
    'exists',
    'not_exists',
];
export const OPERATORS_BY_VALUE_TYPE = {
    text: [
        'equals',
        'not_equals',
        'contains',
        'not_contains',
        'exists',
        'not_exists',
    ],
    boolean: ['equals', 'exists', 'not_exists'],
    choice: ['equals', 'not_equals', 'exists', 'not_exists'],
    id: ['equals', 'not_equals', 'exists', 'not_exists'],
    integer: ORDERED,
    date: ORDERED,
};
/* ---------- system and contact variables ---------- */
/**
 * What a graph can read about the company and the moment. `is_business_hours`
 * is "yes" when the current time is inside the company's business hours, in its
 * time zone, and "no" when it is outside them.
 */
export const SYSTEM_FIELDS = ['is_business_hours'];
/**
 * What a graph can read about the caller from their CRM record. `group` is a
 * role the caller has on a property, one of the CRM's relationship levels.
 * `property` is a property's CRM id, `property_group` a group a property is
 * in. A caller can have several roles and properties; a check holds when any
 * of them matches, and "does not equal" when none does.
 */
export const CONTACT_FIELDS = [
    'name',
    'email',
    'phone',
    'group',
    'property_name',
    'property_address',
    'property_unit',
    'property',
    'property_group',
];
/* ---------- rows ---------- */
/**
 * Holds once its event has been raised. A graph event (do not call, asks for
 * a human, any event the graph listens for) counts from anywhere in the call.
 * A node event (a booking, a transfer outcome, a line finished) counts when
 * `nodeId` raised it; with no `nodeId`, when the node the edge leaves raised
 * it, or any node for an edge from anywhere.
 */
export const eventRowSchema = z.object({
    kind: z.literal('event'),
    event: snakeKey,
    nodeId: uuid.optional(),
});
/**
 * What a value row reads: a system or contact variable, a form question by its
 * node and item id, or a conversation node's signal by its node and signal id.
 * Authors see a question or signal by its item id, as `{item}`; the node keeps
 * two forms that use the same item id apart.
 */
export const valueRefSchema = z.discriminatedUnion('source', [
    z.object({ source: z.literal('system'), field: z.enum(SYSTEM_FIELDS) }),
    z.object({ source: z.literal('contact'), field: z.enum(CONTACT_FIELDS) }),
    z.object({ source: z.literal('question'), nodeId: uuid, item: snakeKey }),
    z.object({ source: z.literal('signal'), nodeId: uuid, item: snakeKey }),
]);
/**
 * Compares an accepted value with a fixed one. A question's accepted value is
 * a reply that conformed, a scratchpad value the question was skipped for, or
 * one the caller confirmed. A signal's is its latest clear reading, true or
 * false. A reply that did not fit is no value. `value` is absent for exists /
 * not_exists; otherwise a boolean for a boolean, a number for an integer and a
 * string for the rest.
 */
export const valueRowSchema = z.object({
    kind: z.literal('value'),
    ref: valueRefSchema,
    op: z.enum(EQUATION_OPERATORS),
    value: z.union([z.string(), z.number(), z.boolean()]).optional(),
});
export const QUESTION_RESULTS = [
    'conforms',
    'off_type',
    'unclear',
    'skipped',
];
export const FORM_RESULTS = ['completed', 'required_complete'];
export const ACTION_RESULTS = ['completed', 'failed'];
/**
 * An output of the node the arrow leaves: how one of its questions went, how
 * the form went, or how its actions went.
 *
 * Question: the answer was expected (`conforms`; a scratchpad value the caller
 * confirmed counts), unexpected (`off_type`; a scratchpad value the caller
 * denied counts), unclear after the retry, or not applicable because the
 * scratchpad had it (`skipped`). Form: the item walk ended (`completed`), or every
 * required question has an accepted value (`required_complete`; a valid "no"
 * counts). Action: with `actionId`, that action of the node's group completed
 * or failed; without it, the group: `completed` when an action completed and
 * none failed, `failed` when one failed. An action that does not run where the
 * node runs is neither.
 */
export const resultRowSchema = z.discriminatedUnion('source', [
    z.object({
        kind: z.literal('result'),
        source: z.literal('question'),
        nodeId: uuid,
        item: snakeKey,
        is: z.enum(QUESTION_RESULTS),
    }),
    z.object({
        kind: z.literal('result'),
        source: z.literal('form'),
        nodeId: uuid,
        is: z.enum(FORM_RESULTS),
    }),
    z.object({
        kind: z.literal('result'),
        source: z.literal('action'),
        nodeId: uuid,
        /** A `WorkflowAction` id in the node's group; absent for the whole group. */
        actionId: z.string().min(1).optional(),
        is: z.enum(ACTION_RESULTS),
    }),
]);
export const equationRowSchema = z.union([
    eventRowSchema,
    valueRowSchema,
    resultRowSchema,
]);
export const EQUATION_MODES = ['all', 'any'];
/**
 * "all" holds when every row holds, "any" when one does. A row reads what the
 * call knows now: "does not exist" means the value is not known yet, and can
 * change when a later node learns it. "Asked and got no usable answer" is a
 * result row. No rows is no condition: only an edge out of a say, transfer or
 * action node can have none.
 */
export const equationConditionSchema = z.object({
    type: z.literal('equation'),
    mode: z.enum(EQUATION_MODES),
    rows: z.array(equationRowSchema),
});
/* ---------- variables ---------- */
/** How each system and contact variable compares. */
export const FIELD_VALUE_TYPES = {
    is_business_hours: 'boolean',
    name: 'text',
    email: 'text',
    phone: 'text',
    group: 'choice',
    property_name: 'text',
    property_address: 'text',
    property_unit: 'text',
    property: 'id',
    property_group: 'id',
};
/** A variable as authors see it: `{contact_name}`, `{property_zip}`. */
export function variableName(ref) {
    if (ref.source === 'system')
        return `{${ref.field}}`;
    if (ref.source === 'contact') {
        return ref.field.startsWith('property')
            ? `{${ref.field}}`
            : `{contact_${ref.field}}`;
    }
    return `{${ref.item}}`;
}
/* ---------- comparing and copying conditions ---------- */
/** A row as a string that does not depend on key order. */
export function equationRowKey(row) {
    return stableStringify(row);
}
/**
 * The one form a condition is stored and compared in: an equation of one
 * event with no node is that event, and no rows or one row is "all" whatever
 * the mode.
 */
export function canonicalCondition(condition) {
    if (condition.type !== 'equation' || condition.rows.length > 1) {
        return condition;
    }
    if (condition.rows.length === 0)
        return { ...condition, mode: 'all' };
    const [only] = condition.rows;
    if (only.kind === 'event' && only.nodeId === undefined) {
        return { type: 'event', event: only.event };
    }
    return { ...condition, mode: 'all' };
}
/** The condition with every node id its rows name passed through `rename`. */
export function renameConditionNodes(condition, rename) {
    if (condition.type !== 'equation')
        return condition;
    return {
        ...condition,
        rows: condition.rows.map(row => {
            if (row.kind === 'result')
                return { ...row, nodeId: rename(row.nodeId) };
            if (row.kind === 'event') {
                return row.nodeId ? { ...row, nodeId: rename(row.nodeId) } : row;
            }
            if (row.ref.source === 'question' || row.ref.source === 'signal') {
                return { ...row, ref: { ...row.ref, nodeId: rename(row.ref.nodeId) } };
            }
            return row;
        }),
    };
}
function stableStringify(value) {
    if (Array.isArray(value))
        return `[${value.map(stableStringify).join(',')}]`;
    if (value && typeof value === 'object') {
        const entries = Object.entries(value)
            .filter(([, v]) => v !== undefined)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
        return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`).join(',')}}`;
    }
    return JSON.stringify(value);
}
/* ---------- builders ---------- */
/** Holds when every row holds. */
export function allOf(first, ...rest) {
    return {
        type: 'equation',
        mode: 'all',
        rows: [first, ...rest],
    };
}
/** Holds when one row holds. */
export function anyOf(first, ...rest) {
    return {
        type: 'equation',
        mode: 'any',
        rows: [first, ...rest],
    };
}
/** A system variable has this value, such as `{is_business_hours}` false. */
export function systemEquals(field, value) {
    return {
        kind: 'value',
        ref: { source: 'system', field },
        op: 'equals',
        value,
    };
}
/** A signal on a conversation node was heard true or false. */
export function signalEquals(nodeId, item, value) {
    return {
        kind: 'value',
        ref: { source: 'signal', nodeId, item },
        op: 'equals',
        value,
    };
}
/** A form question's accepted answer is this value. */
export function answerEquals(nodeId, item, value) {
    return {
        kind: 'value',
        ref: { source: 'question', nodeId, item },
        op: 'equals',
        value,
    };
}
/** How a form question went. */
export function questionResult(nodeId, item, is) {
    return { kind: 'result', source: 'question', nodeId, item, is };
}
/** How a form went. */
export function formResult(nodeId, is) {
    return { kind: 'result', source: 'form', nodeId, is };
}
/** How an action node's group went, or with `actionId` one of its actions. */
export function actionResult(nodeId, is, actionId) {
    return actionId === undefined
        ? { kind: 'result', source: 'action', nodeId, is }
        : { kind: 'result', source: 'action', nodeId, actionId, is };
}
//# sourceMappingURL=equation.js.map